'use client';

import { useEffect, useRef } from 'react';
import { useDarkMode } from "../../utils/hooks/useDarkMode";
import { loadTrustpilotWidget } from "../../utils/services/trustpilotService";

/**
 * TrustpilotWidget - Unified component supporting both static and dynamic modes
 * 
 * MODE CONTROL (via environment variable):
 * - NEXT_PUBLIC_TRUSTPILOT_MODE="dynamic" → Loads actual Trustpilot widget (Site A)
 * - NEXT_PUBLIC_TRUSTPILOT_MODE="static" or undefined → Shows fixed rating (Site B, C)
 * 
 * STATIC MODE CONFIGURATION:
 * - NEXT_PUBLIC_TRUSTPILOT_RATING="4.7" → Fixed rating value
 * - Or pass rating prop: rating="4.7"
 * 
 * @param {string} mode - Override mode: "static" | "dynamic" (defaults to env var)
 * @param {string} textColor - Custom text color class (e.g., "text-white")
 * @param {string} backgroundColor - Background color (default: 'transparent')
 * @param {string} fontSize - Font size class (default: 'text-xs')
 * @param {string} className - Additional CSS classes
 * @param {string} rating - Rating value for static mode (defaults to NEXT_PUBLIC_TRUSTPILOT_RATING or "4.7")
 * @param {boolean} showIntroText - Show intro text before rating (static mode only)
 * @param {string} introText - Custom intro text (default: "Our Customers say:")
 * @param {"light"|"dark"} theme - Trustpilot theme override ("dark" = light text for dark backgrounds)
 */
export default function TrustpilotWidget({ 
  mode = null,
  textColor = null, 
  backgroundColor = 'transparent', 
  fontSize = 'text-xs',
  className = 'items-center justify-center',
  rating = null,
  showIntroText = false,
  introText = "Our Customers say:",
  theme = null,
}) {
  const { isDarkMode } = useDarkMode();
  const widgetRef = useRef(null);
  const isInitialized = useRef(false);
  const trustpilotTheme = theme || (isDarkMode ? "dark" : "light");

  // Determine mode: prop > environment variable > default to static
  const widgetMode = mode || 
    process.env.NEXT_PUBLIC_TRUSTPILOT_MODE || 
    'static';

  // Dynamic mode: Use Trustpilot widget service
  useEffect(() => {
    if (widgetMode === 'dynamic' && widgetRef.current && !isInitialized.current) {
      loadTrustpilotWidget(widgetRef.current, false);
      isInitialized.current = true;
    }
  }, [widgetMode, trustpilotTheme]);

  // If dynamic mode, render Trustpilot widget
  if (widgetMode === 'dynamic') {
    return (
      <div
        ref={widgetRef}
        className="trustpilot-widget"
        data-locale="en-GB"
        data-template-id="5419b637fa0340045cd0c936"
        data-businessunit-id="652d0d149b896e538a303969"
        data-style-height="24px"
        data-style-width="100%"
        data-theme={trustpilotTheme}
      >
        <a
          href="https://uk.trustpilot.com/review/hirego.co.uk"
          target="_blank"
          rel="noopener noreferrer"
        >
          Trustpilot
        </a>
      </div>
    );
  }

  // Static mode: Show fixed rating
  const displayRating = rating || 
    process.env.NEXT_PUBLIC_TRUSTPILOT_RATING || 
    '4.7';

  const getTextColor = () => {
    if (textColor) return textColor;
    return isDarkMode ? 'text-white' : 'text-gray-900';
  };

  return (
    <div 
      className={`flex items-center justify-center flex-wrap gap-1 ${className}`}
      style={{ backgroundColor }}
    >
      {showIntroText && (
        <span className={`${fontSize} font-medium ${getTextColor()}`}>
          {introText}
        </span>
      )}
      <span className={`${fontSize} font-medium ${getTextColor()}`}>
        Excellent {displayRating} OUT OF 5 BASED ON
      </span>
      <div className="flex items-center gap-1">
        <svg 
          className="w-4 h-4 text-green-500 flex-shrink-0" 
          fill="currentColor" 
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        <span className={`${fontSize} font-medium ${getTextColor()}`}>
          Trustpilot
        </span>
      </div>
    </div>
  );
}
